import type { MonthData, InventoryRow, Recipe, BatchRun } from "../types";
import * as storage from "./storage";

// ── Public API ────────────────────────────────────────────────────────

export { pickLedgerDir, getLedgerDir, hasLedgerDir } from "./storage";

export async function readMonth(month: string): Promise<MonthData> {
  return storage.readMonth(month);
}

export async function writeMonth(month: string, data: MonthData): Promise<void> {
  return storage.writeMonth(month, data);
}

export async function listMonths(): Promise<string[]> {
  return storage.listMonths();
}

export async function appendToLedger(month: string, section: string, rows: InventoryRow[]): Promise<void> {
  const data = await storage.readMonth(month);
  if (section === "raw_materials") data.raw_materials.push(...rows);
  else if (section === "packaging") data.packaging.push(...rows);
  else if (section === "finished_goods") data.finished_goods.push(...rows);
  else throw new Error(`Unknown section: ${section}`);
  await storage.writeMonth(month, data);
}

// ── Date / Maths Helpers ──────────────────────────────────────────────

export function todayString(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function computeTotal(qty: number, price: number): number {
  return qty * price;
}

export function createRow(item = "", quantity = 1, unit = "kg", unitPrice = 0): InventoryRow {
  return { item, quantity, unit, unit_price: unitPrice, total: computeTotal(quantity, unitPrice) };
}

export function recalcRows(rows: InventoryRow[]): InventoryRow[] {
  return rows.map((r) => ({ ...r, total: computeTotal(r.quantity, r.unit_price) }));
}

export function grandTotal(rows: InventoryRow[]): number {
  return rows.reduce((sum, r) => sum + r.total, 0);
}

// ── Recipe Functions ──────────────────────────────────────────────────

const RECIPES_FILE = "recipes.md";

function parseRecipes(markdown: string): Recipe[] {
  const recipes: Recipe[] = [];
  let current: Recipe | null = null;

  for (const line of markdown.split("\n")) {
    const t = line.trim();
    if (t.startsWith("## ")) {
      if (current) recipes.push(current);
      current = { name: t.slice(3).trim(), finishesGood: "", unit: "", ingredients: [] };
    } else if (current) {
      if (t.startsWith("- finished_good:")) current.finishesGood = t.slice(16).trim();
      else if (t.startsWith("- unit:")) current.unit = t.slice(7).trim();
      else if (t.startsWith("- item:") && t.includes("qty:") && t.includes("unit:") && t.includes("section:")) {
        const parts = t.slice(2).split(",").reduce(
          (acc, p) => {
            const [k, ...v] = p.split(":");
            acc[k.trim()] = v.join(":").trim();
            return acc;
          },
          {} as Record<string, string>
        );
        current.ingredients.push({
          item: parts["item"] || "",
          quantity: parseFloat(parts["qty"]) || 0,
          unit: parts["unit"] || "",
          section: (parts["section"] as "raw_materials" | "packaging") || "raw_materials",
        });
      }
    }
  }
  if (current) recipes.push(current);
  return recipes;
}

function serializeRecipes(recipes: Recipe[]): string {
  const lines: string[] = ["# Mavelux Recipes", ""];
  for (const r of recipes) {
    lines.push(`## ${r.name}`);
    lines.push(`- finished_good: ${r.finishesGood}`);
    lines.push(`- unit: ${r.unit}`);
    lines.push("");
    for (const ing of r.ingredients) {
      lines.push(`  - item: ${ing.item}, qty: ${ing.quantity}, unit: ${ing.unit}, section: ${ing.section}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

export async function readRecipes(): Promise<Recipe[]> {
  const content = await storage.readLedgerFile(RECIPES_FILE);
  if (!content.trim()) return [];
  return parseRecipes(content);
}

export async function writeRecipes(recipes: Recipe[]): Promise<void> {
  const content = serializeRecipes(recipes);
  await storage.writeLedgerFile(RECIPES_FILE, content);
}

/** Look up the most recent unit_price for a given item name in a section */
function findPrice(rows: InventoryRow[], itemName: string): number {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].item.toLowerCase() === itemName.toLowerCase()) return rows[i].unit_price;
  }
  return 0;
}

/** Execute a production batch — deducts ingredients, adds finished goods */
export async function runBatch(month: string, recipe: Recipe, run: BatchRun): Promise<void> {
  const data = await storage.readMonth(month);
  const date = todayString();

  for (const ing of recipe.ingredients) {
    const usedQty = -(ing.quantity * run.batchQty);
    const wasteQty = -(ing.quantity * run.batchQty * (run.wastePct / 100));
    const totalDeduction = usedQty + wasteQty;
    const price = findPrice(data[ing.section], ing.item);
    data[ing.section].push({
      item: `${ing.item} (used)`,
      quantity: totalDeduction,
      unit: ing.unit,
      unit_price: price,
      total: totalDeduction * price,
    });
  }

  data.finished_goods.push({
    item: recipe.finishesGood,
    quantity: run.batchQty,
    unit: recipe.unit,
    unit_price: run.unitPrice,
    total: run.batchQty * run.unitPrice,
  });

  await storage.writeMonth(month, data);
}
